import { Box, CircularProgress, Paper, Typography } from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { CheckCircle } from '@material-ui/icons';
import { Layout, useBaseStore } from '@roc/feature-app-core';
import { Button, StepsContainer, VerticalBarLoader } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createSecureLinkClient } from '@roc-capital/secure-link';
import { useSecureLinkSession } from '@roc-capital/secure-link/react';
import { useStore } from 'apps/client-portal-public/src/app/hooks';
import { ReferralAuthWelcome } from './welcome';
import { BankInformation } from './bankInformation';
import { ReferralFeeSignatureStep } from './referralFeeSignatureStep';
import { REFERRAL_FEE_BANK_INFO_PURPOSE } from './referralFeeUtils';

const useStyles = makeStyles(theme =>
  createStyles({
    stepper: {
      [theme.breakpoints.down('xs')]: {
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
      },
      maxWidth: 1000,
      margin: 'auto',
      '& .MuiStepLabel-completed': {
        color: theme.palette.primary.main,
      },
    },
    box: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%'
    },
    paper: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2)
    }
  })
);

const Message = ({ title, text, action }: { title: string; text: string; action?: { label: string; onClick: () => void } }) => (
  <Box textAlign="center" py={6} px={2} maxWidth={520} mx="auto">
    <Typography variant="h5" gutterBottom>
      {title}
    </Typography>
    <Typography color="textSecondary">{text}</Typography>
    {action && (
      <Box mt={3}>
        <Button testId="retry" variant="outlined" color="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      </Box>
    )}
  </Box>
);

const PageTitle = () => (
  <Box display={'flex'} justifyContent={'center'}>
    <Typography variant="h3">Referral Fee Authorization</Typography>
  </Box>
);

/**
 * The referral fee page for secure links: /link/referral-fee. The broker arrives here from the platform landing page
 * (/platform-app/link#token), which started the session; every call is authorized by that session, and the loan comes
 * from it on the server. Replaces /referral-fee/:token, whose token carried the loan id.
 */
export const SecureLinkReferralFeePage = observer(() => {
  const classes = useStyles();
  const { environmentStore } = useBaseStore();
  const client = useMemo(
    () => createSecureLinkClient({ apiBaseUrl: environmentStore.environmentVariables.restApiBaseURL ?? '' }),
    []
  );
  const { status, request, retry } = useSecureLinkSession(client, { purpose: REFERRAL_FEE_BANK_INFO_PURPOSE });
  const { referralAuthStore } = useStore();

  // A layout effect, so the store has the session's request before the data fetch below runs on the same commit.
  useLayoutEffect(() => referralAuthStore.setRequest(request), [referralAuthStore, request]);

  return (
    <Layout maxWidth={'lg'}>
      <PageTitle />
      {status === 'loading' && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}
      {status === 'ended' && (
        <Paper className={classes.paper}>
          <Message
            title="Your session has ended"
            text="For your security, sessions end after 15 minutes or when the link is opened in another window. Open the link you received again to continue."
          />
        </Paper>
      )}
      {status === 'unavailable' && (
        <Paper className={classes.paper}>
          <Message
            title="We couldn't open this page"
            text="This is usually temporary. Check your connection and try again."
            action={{ label: 'Try again', onClick: retry }}
          />
        </Paper>
      )}
      {status === 'active' && <ReferralAuthBody />}
    </Layout>
  );
});

/**
 * /referral-fee/:token, the old link format (a token carrying the loan id). Those links no longer work: the lender
 * shares a new one, which opens /link/referral-fee.
 */
export const ReferralFeeLinkReplacedPage = () => {
  const classes = useStyles();
  return (
    <Layout maxWidth={'lg'}>
      <PageTitle />
      <Paper className={classes.paper}>
        <Message
          title="This link is no longer valid"
          text="Please ask the lender for a new link to provide your bank information."
        />
      </Paper>
    </Layout>
  );
};

const ReferralAuthBody = observer(() => {
  const { referralAuthStore } = useStore();
  const [showWelcome, setShowWelcome] = useState(true);

  const classes = useStyles();

  useEffect(() => {
    referralAuthStore.loadData();
  }, [referralAuthStore]);

  const steps = [
    {
      title: 'Bank Information',
      icon: null,
      completed: referralAuthStore.activeStep > 0,
    },
    {
      title: 'Electronic Signature',
      icon: null,
      completed: referralAuthStore.isSigned,
    }
  ];

  return (
    <>
      {referralAuthStore.loading && <VerticalBarLoader />}
      {referralAuthStore.requestComplete && <Paper className={classes.paper}>
        {referralAuthStore.isSigned && <CompletedView />}
        {!referralAuthStore.isSigned && (
          <StepsContainer
            disableNavigation={true}
            activeStep={referralAuthStore.activeStep}
            handleStep={step => {
              referralAuthStore.activeStep = step;
            }}
            steps={steps}
            stepperClassName={classes.stepper}
          >
            {showWelcome ? <ReferralAuthWelcome onStart={() => setShowWelcome(false)} /> : <BankInformation />}
            <ReferralFeeSignatureStep />
          </StepsContainer>
        )}
      </Paper>}
    </>
  );
});

const CompletedView = () => (
  <Box mt={4} mx={'auto'} textAlign="center">
    <CheckCircle style={{ fontSize: 60, color: green[500] }} />
    <br />
    <Typography variant="h4" color="textPrimary">
      Process Completed!
    </Typography>
    <br />
    <Typography>The referral fee authorization process has been completed.</Typography>
    <Typography>Your information was successfully received.</Typography>
    <br />
  </Box>
);
